<script setup lang="ts">
import type { HTMLAttributes } from 'vue';
import { computed, onBeforeUnmount, shallowRef, toRef, watch } from 'vue';
import { imgVariants, type OssImageParams } from '.';
import IconImgError from '../../assets/img-load-fail.svg?component';
import IconImgLoading from '../../assets/img-loading.svg?component';
import { transformImageUrl } from '../../libs/oss-image';
import { cn } from '../../libs/utils';

interface Props {
  src: string;
  alt?: string;
  width?: number | string;
  height?: number | string;
  objectFit?: 'cover' | 'contain' | 'fill' | 'none' | 'scaleDown';
  loading?: 'lazy' | 'eager';
  class?: HTMLAttributes['class'];
  // OSS 图片处理参数
  ossParams?: OssImageParams;
  // 是否启用 OSS 处理（默认启用）
  enableOss?: boolean;
  // 是否显示加载状态（默认关闭）
  showLoading?: boolean;
  // 加载失败时的占位图
  fallback?: string;
  // 图片加载失败时的回调
  onError?: (event: Event) => void;
  // 图片加载成功时的回调
  onLoad?: (event: Event) => void;
}

const props = withDefaults(defineProps<Props>(), {
  objectFit: 'cover',
  loading: 'lazy',
  enableOss: true,
  showLoading: false,
});

const emit = defineEmits<{
  error: [event: Event];
  load: [event: Event];
}>();

// 图片加载状态 - 使用 shallowRef 减少深度响应式开销
type ImageLoadState = 'loading' | 'loaded' | 'error';
// 初始状态根据 showLoading 配置决定
const loadState = shallowRef<ImageLoadState>(props.showLoading ? 'loading' : 'loaded');

// 使用 toRef 优化响应式性能
const src = toRef(props, 'src');
const showLoading = toRef(props, 'showLoading');
const enableOss = toRef(props, 'enableOss');

// 检测是否是 inline SVG content - 缓存结果避免重复计算
const isInlineSvg = computed(() => {
  const srcValue = src.value;
  return srcValue && (srcValue.startsWith('data:image/svg+xml') || srcValue.includes('<svg'));
});

// 处理后的图片 URL - 缓存计算结果
const processedSrc = computed(() => {
  const srcValue = src.value;
  if (!srcValue) return '';

  // 如果禁用了 OSS 处理，直接返回原 URL
  if (!enableOss.value) {
    return srcValue;
  }

  return transformImageUrl(srcValue, props.ossParams);
});

// 处理图片加载错误
const handleError = (event: Event) => {
  loadState.value = 'error';
  emit('error', event);
  props.onError?.(event);
};

// 处理图片加载成功
const handleLoad = (event: Event) => {
  loadState.value = 'loaded';
  emit('load', event);
  props.onLoad?.(event);
};

// 预加载图片引用，用于清理
let preloadImg: HTMLImageElement | null = null;

// 简化的状态计算 - 合并多个 computed 为一个
const displayState = computed(() => {
  const srcValue = src.value;
  const currentLoadState = loadState.value;
  const shouldShowLoadingState = showLoading.value && !isInlineSvg.value;

  // 如果没有 src，显示错误状态
  if (!srcValue) {
    return { showLoading: false, showError: true, showImage: false };
  }

  // 如果是 inline SVG，直接显示图片
  if (isInlineSvg.value) {
    return { showLoading: false, showError: false, showImage: true };
  }

  // 根据加载状态和配置决定显示内容
  if (currentLoadState === 'error') {
    return { showLoading: false, showError: true, showImage: false };
  }

  // 如果不需要显示 loading 状态，直接显示图片
  if (!shouldShowLoadingState) {
    return { showLoading: false, showError: false, showImage: true };
  }

  // 需要显示 loading 状态时的逻辑
  if (currentLoadState === 'loading') {
    return { showLoading: true, showError: false, showImage: false };
  }

  // 加载完成，显示图片
  return { showLoading: false, showError: false, showImage: true };
});

// 监听 src 变化，重置状态
watch(
  processedSrc,
  (newSrc, oldSrc) => {
    if (newSrc !== oldSrc) {
      // 如果有新的 src 且需要显示 loading，设置为 loading 状态
      if (newSrc && showLoading.value && !isInlineSvg.value) {
        loadState.value = 'loading';
        // 清理之前的预加载图片
        if (preloadImg) {
          preloadImg.onload = null;
          preloadImg.onerror = null;
        }
        // 预加载图片以检测是否已缓存
        preloadImg = new Image();
        preloadImg.onload = () => {
          loadState.value = 'loaded';
        };
        preloadImg.onerror = () => {
          loadState.value = 'error';
        };
        preloadImg.src = newSrc;
      } else if (newSrc) {
        // 如果不需要显示 loading（比如 inline SVG），直接设为 loaded
        loadState.value = 'loaded';
      } else {
        loadState.value = 'loading';
      }
    }
  },
  { immediate: true },
);

// 清理资源
onBeforeUnmount(() => {
  // 清理预加载图片
  if (preloadImg) {
    preloadImg.onload = null;
    preloadImg.onerror = null;
    preloadImg.src = '';
    preloadImg = null;
  }
  // 清理状态
  loadState.value = 'loading';
});
</script>

<template>
  <div
    class="img-container"
    :class="props.class"
    :style="{
      width: typeof width === 'number' ? `${width}px` : width,
      height: typeof height === 'number' ? `${height}px` : height,
    }"
  >
    <!-- Loading 状态 -->
    <div v-if="displayState.showLoading" class="img-loading img-loading--shimmer">
      <IconImgLoading class="img-loading__icon" />
    </div>

    <!-- Error 状态 -->
    <div v-else-if="displayState.showError" class="img-error">
      <IconImgError class="img-error__icon" />
      <div v-if="fallback" class="img-error__fallback">
        {{ fallback }}
      </div>
    </div>

    <!-- 图片 -->
    <img
      v-if="displayState.showImage"
      data-slot="img"
      :src="processedSrc"
      :alt="alt"
      :loading="loading"
      :class="cn('img-element', imgVariants({ objectFit }))"
      @error="handleError"
      @load="handleLoad"
    />
  </div>
</template>

<style scoped lang="scss">
.img-container {
  position: relative;
  display: block;
  width: 100%;
  height: 100%;
  // 性能优化 - 启用 CSS contain 提升渲染性能
  contain: layout style paint;
  // 优化层叠上下文
  isolation: isolate;
}

.img-element {
  width: 100%;
  height: 100%;
  display: block;
}

// Loading 状态样式（简化版，无大面积扫光动画）
.img-loading {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  width: 100%;
  background-color: var(--x-back-3);
  contain: layout style paint;

  &__icon {
    width: 40px;
    height: 40px;
    color: var(--x-back-5);
    z-index: 1;
  }
}

// Error 状态样式
.img-error {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background-color: var(--x-back-3);
  // 性能优化
  contain: layout style paint;

  &__icon {
    width: 80px;
    height: 80px;
    color: var(--x-back-5);
    margin-bottom: 8px;
  }

  &__fallback {
    font-size: 12px;
    color: #6b7280;
    text-align: center;
    padding: 0 8px;
    // 文本渲染优化
    text-rendering: optimizeSpeed;
  }
}

// 预留媒体查询，后续如需要可在此增加更细粒度的动效控制
@media (prefers-reduced-motion: reduce) {
  .img-loading {
    animation: none;
  }
}
</style>
